import React, { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ScoutAIButtonProps {
  companyName: 'dunder' | 'wonka' | 'initech';
}
const companyNotes = {
  dunder: {
    title: 'Dunder Mifflin Paper Company',
    summary: 'Pulse Score of 72 indicates low risk with stable operations. Revenue is flat YoY at $12M but profitability remains consistent with reliable 3-day DBT. Current $35K credit limit is appropriate; monitor Q2 performance for potential increase.',
    insights: ['Pulse Score of 68 indicates moderate risk with stable operations', 'Revenue flat YoY at $12M, but maintaining consistent profitability', 'Payment behavior is reliable with 15-day average DBT', 'Long operating history (33 years) provides stability', 'Recent large order expected in Q2 from new client'],
    recommendation: 'Current $35K credit limit is appropriate. Monitor Q2 performance for potential increase.',
    riskLevel: 'moderate' as const
  },
  wonka: {
    title: 'Wonka Industries',
    summary: 'Pulse Score of 88 indicates low risk with strong growth trajectory. Revenue up 18% YoY to $28M with excellent 5-day DBT and expanding market share. Strong candidate for credit limit increase to $100K+ based on consistent growth and payment performance.',
    insights: ['Pulse Score of 82 indicates low risk with strong growth trajectory', 'Revenue up 18% YoY to $28M with expanding market share', 'Excellent payment history with 8-day average DBT', 'R&D investment driving innovation in premium chocolate segment', 'Headcount growing 8% annually, signaling expansion'],
    recommendation: 'Strong candidate for credit limit increase. Consider raising to $100K+ based on growth.',
    riskLevel: 'low' as const
  },
  initech: {
    title: 'Initech Corporation',
    summary: 'Pulse Score of 28 indicates very high risk requiring immediate review. Revenue down 35% YoY with negative cash flow, DBT spiked from 15 to 45 days, and using 91% of available credit. Recommend suspending credit immediately—company deteriorated dramatically from score of 78 in April 2025.',
    insights: ['Pulse Score of 28 indicates very high risk - immediate review required', 'Revenue down 35% YoY with negative cash flow and EBITDA', 'Payment behavior deteriorated: DBT spiked from 15 to 45 days', 'Two C-level executives departed in Q4 2025', 'Recent cybersecurity breach in November 2025 compounds operational risk', 'Using 91% of available credit ($68K/$75K)'],
    recommendation: 'Recommend suspending credit immediately. Company was healthy in April 2025 (score 78) but deteriorated dramatically over 10 months.',
    riskLevel: 'high' as const
  }
};
export function ScoutAIButton({
  companyName
}: ScoutAIButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const notes = companyNotes[companyName];
  // Pulse every 30 seconds until clicked
  useEffect(() => {
    if (hasBeenClicked) return;
    const pulseInterval = setInterval(() => {
      setShouldPulse(true);
      setTimeout(() => setShouldPulse(false), 2000);
    }, 30000);
    // Initial pulse after 2 seconds
    const initialPulse = setTimeout(() => {
      setShouldPulse(true);
      setTimeout(() => setShouldPulse(false), 2000);
    }, 2000);
    return () => {
      clearInterval(pulseInterval);
      clearTimeout(initialPulse);
    };
  }, [hasBeenClicked]);
  const handleClick = () => {
    setIsOpen(!isOpen);
    setHasBeenClicked(true);
    setShouldPulse(false);
  };
  const getRiskColor = () => {
    switch (notes.riskLevel) {
      case 'low':
        return 'text-green-600';
      case 'moderate':
        return 'text-yellow-600';
      case 'high':
        return 'text-red-600';
    }
  };
  return <>
      {/* Floating AI Button */}
      <div className="fixed bottom-6 right-6 flex items-center gap-3 z-40" data-id="element-3046">
        {/* Chat Bubble - appears when pulsing */}
        <AnimatePresence data-id="element-3047">
          {shouldPulse && !hasBeenClicked && <motion.div initial={{
          opacity: 0,
          x: 10,
          scale: 0.9
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: 10,
          scale: 0.9
        }} className="bg-white rounded-lg shadow-lg px-4 py-2 border border-gray-200" data-id="element-3048">
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap" data-id="element-3049">
                Ask Scout AI
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Scout Overview Chat Bubble - appears when clicked */}
        <AnimatePresence data-id="element-3050">
          {isOpen && <motion.div initial={{
          opacity: 0,
          x: 10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: 10,
          scale: 0.95
        }} className="bg-white rounded-xl shadow-2xl border border-gray-200 w-[380px] max-w-[calc(100vw-120px)]" data-id="element-3051">
              <div className="p-4" data-id="element-3052">
                <div className="flex items-start justify-between mb-3" data-id="element-3053">
                  <div className="flex items-center gap-2" data-id="element-3054">
                    <img src="https://cdn.magicpatterns.com/uploads/fNTWFuWPoWGamQF8RMu8TB/Icon_Scout.png" alt="Scout AI" className="w-6 h-6" data-id="element-3055" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-3056">
                      Scout AI
                    </span>
                  </div>
                  <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3057">
                    <X className="w-4 h-4" data-id="element-3058" />
                  </button>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-3059">
                  {notes.summary}
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Circular Button with Icon */}
        <motion.button onClick={handleClick} className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow bg-white border-2 border-gray-200" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} animate={shouldPulse ? {
        scale: [1, 1.1, 1],
        boxShadow: ['0 10px 15px -3px rgba(0, 0, 0, 0.1)', '0 20px 25px -5px rgba(59, 130, 246, 0.5)', '0 10px 15px -3px rgba(0, 0, 0, 0.1)']
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-3060">
          <img src="https://cdn.magicpatterns.com/uploads/fNTWFuWPoWGamQF8RMu8TB/Icon_Scout.png" alt="Scout AI" className="w-10 h-10" data-id="element-3061" />
        </motion.button>
      </div>
    </>;
}